import React from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';

const assessmentSections = [
  {
    title: 'Holistic Understanding',
    description:
      'Every young person arrives with a unique history and set of experiences. Our assessment pathway brings together clinical interviews, observation within everyday routines, and evidence-based assessment tools to develop a balanced understanding of strengths, areas of concern, and protective factors before any intervention is planned.',
    bullets: [
      'Early screening: Conducted within 72 hours of arrival to identify immediate risks and stabilisation needs.',
      'Multi-disciplinary formulation: Collaborative sessions involving psychologists, therapists, education leads, and care staff to build a shared understanding.',
      'Voice of the child: Central to the process, with adapted communication approaches used where required to ensure each young person can meaningfully contribute.'
    ]
  },
  {
    title: 'Assessment Packages',
    description:
      'Commissioners may commission a single assessment or opt for a combined assessment package. Outcomes are provided in a clear, concise report, with integrated recommendations that inform care planning, clinical intervention, and educational objectives.',
    bullets: [
      'Psychological assessments: Including cognitive functioning, trauma impact, and emotional wellbeing measures.',
      'Occupational therapy assessments: Focused on sensory processing, daily routines, and adaptive skills.',
      'Speech and language assessments: Covering functional communication and social interaction profiles.',
      'Risk assessments: Addressing exploitation, missing episodes, self-harm, and community safety considerations.'
    ]
  },
  {
    title: 'Turning Insight into Planning',
    description:
      'Following each assessment, we facilitate a feedback workshop involving carers, social workers, and, where appropriate, family members. This collaborative session focuses on agreeing clear outcomes, defining measures of success, and identifying the clinical input required to achieve them.',
    bullets: [
      'Actionable recommendations: SMART recommendations translated into practical, day-to-day care strategies.',
      'Progress tracking: Shared dashboards provided to placing authorities to support transparency and accountability.',
      'Ongoing review: A structured review timetable to ensure progress is monitored and plans are adjusted as needed.'
    ]
  }
];

const TherapyAssessments = () => (
  <div className="TherapyAssessments bg-white">
    <Header />
    <div className="mt-32 sm:mt-36 lg:mt-32">
      <Hero
        image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=2000&q=80"
        heightClass="min-h-[50vh] sm:min-h-[60vh] lg:min-h-[70vh]"
        position="center"
      />

      <section className="bg-white py-16 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <p className="text-sm uppercase tracking-[0.3em] text-[#2f4c74] mb-4">
          Therapy Services
        </p>
        <h1 className="text-4xl md:text-6xl font-light text-[#2b3d57] mb-6">
          Assessments
        </h1>
        <p className="text-xl text-[#2b3d57] max-w-4xl leading-relaxed">
          Our Multi-Disciplinary Assessment and Therapy Service provides every child with a thorough baseline assessment
          prior to the start of any intervention. The assessment process is grounded in relational safety and
          trauma-informed practice, with a strong emphasis on clear, practical next steps. This ensures that all
          professionals and carers supporting the young person have a shared understanding of their needs and how best
          to support them.
        </p>
      </div>
    </section>

    <div className="bg-[#f2f4f7] py-16 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto space-y-12">
        {assessmentSections.map((section) => (
          <div key={section.title} className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-3xl font-light text-[#2b3d57] mb-4">{section.title}</h2>
            <p className="text-base text-[#2b3d57] mb-4 leading-relaxed">{section.description}</p>
            {section.bullets && (
              <ul className="list-disc list-inside space-y-2 text-[#2b3d57]">
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>

    <section className="bg-white py-16 px-6 lg:px-12">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h3 className="text-3xl font-light text-[#2b3d57] mb-4">Sharing Outcomes</h3>
          <p className="text-[#2b3d57] leading-relaxed">
            All reports are written in clear, accessible language, with executive summaries for commissioners, practical
            guidance for frontline teams, and easy-read versions for young people. Findings can be presented either
            virtually or in person, and the team remains available for follow-up discussion to support understanding and
            implementation.
          </p>
        </div>
        <div className="bg-[#e9edf3] p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-[#2b3d57] mb-2">Need an Assessment?</h4>
          <p className="text-[#2b3d57] mb-4">
            Speak with our clinical coordination team to explore referral criteria, timescales, and how we can tailor an
            assessment to your specific requirements.
          </p>
          <a
            href="mailto:Info@testacare.co.uk"
            className="inline-flex items-center justify-center rounded-full border-2 border-[#2f4c74] px-6 py-2 text-[#2b3d57] transition hover:bg-[#2f4c74] hover:text-white"
          >
            Talk to us
          </a>
        </div>
      </div>
    </section>

      <Footer />
    </div>
  </div>
);

export default TherapyAssessments;


